import { useState } from "react";
import { ImageOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { formatTripDateRange } from "@/lib/trip-datetime";
import { formatLocationLabel } from "@/lib/trip-location";

export interface DriverInvitationCarSummaryData {
  car: {
    name: string | null;
    year: number | null;
    imageUrl: string | null;
    city: string | null;
    region: string | null;
  } | null;
  startAt: string | null;
  endAt: string | null;
  timesCaptured: boolean;
}

interface Props {
  summary: DriverInvitationCarSummaryData | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

function resolveImageUrl(url: string | null | undefined): string | null {
  const raw = url?.trim();
  if (!raw) return null;
  if (/^(https?:|data:|blob:|\/\/)/i.test(raw)) return raw;
  const base = (import.meta.env.BASE_URL ?? "/").replace(/\/+$/, "");
  return raw.startsWith("/") ? `${base}${raw}` : `${base}/${raw}`;
}

const shell =
  "flex w-full items-stretch overflow-hidden rounded-xl border border-border bg-card text-left shadow-xs";

export function DriverInvitationCarSummary({ summary, loading, error, onRetry }: Props) {
  const { t, i18n } = useAppTranslation();
  const car = summary?.car ?? null;
  const src = resolveImageUrl(car?.imageUrl);
  // Bind failures to the source. A cached error can arrive before passive effects;
  // resetting a boolean there erases the failure and shows the broken image again.
  const [brokenSource, setBrokenSource] = useState<string | null>(null);

  if (loading) {
    return (
      <div
        className={shell}
        role="status"
        aria-busy="true"
        data-testid="driver-invitation-summary-loading"
      >
        <span className="sr-only">{t("booking.primary_driver.summary_loading")}</span>
        <div aria-hidden className="aspect-[4/3] w-28 shrink-0 animate-pulse bg-muted motion-reduce:animate-none sm:w-36" />
        <div aria-hidden className="flex flex-1 flex-col justify-center gap-2.5 p-4">
          <div className="h-4 w-3/4 animate-pulse rounded bg-muted motion-reduce:animate-none" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-muted motion-reduce:animate-none" />
          <div className="h-3 w-2/5 animate-pulse rounded bg-muted motion-reduce:animate-none" />
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-border bg-card p-4 text-left shadow-xs"
        role="alert"
        data-testid="driver-invitation-summary-error"
      >
        <p className="text-sm text-muted-foreground">
          {t("booking.primary_driver.summary_failed")}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0 rounded-full"
          onClick={onRetry}
          data-testid="button-driver-invitation-summary-retry"
        >
          {t("booking.primary_driver.summary_retry")}
        </Button>
      </div>
    );
  }

  const name = (car?.name ?? "").trim();
  const year = car?.year ? String(car.year) : "";
  const title = name
    ? year && !name.includes(year)
      ? `${year} ${name}`
      : name
    : year;
  const place = formatLocationLabel(car?.city, car?.region);
  const dates = formatTripDateRange(summary.startAt, summary.endAt, {
    timesCaptured: summary.timesCaptured,
    locale: i18n.language,
  });
  const showImage = !!src && src !== brokenSource;
  const photoLabel = t("booking.primary_driver.summary_photo_missing");

  return (
    <section
      className={shell}
      aria-label={title || t("booking.primary_driver.summary_car_missing")}
      data-testid="driver-invitation-summary"
    >
      <div className="relative aspect-[4/3] w-28 shrink-0 self-stretch bg-muted sm:w-40">
        {showImage ? (
          <img
            src={src!}
            alt={title || t("booking.primary_driver.summary_car_missing")}
            loading="lazy"
            decoding="async"
            onError={() => setBrokenSource(src)}
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="img-driver-invitation-car"
          />
        ) : (
          <div
            role="img"
            aria-label={photoLabel}
            className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-brand-tint px-2 text-center text-brand-deep dark:bg-secondary dark:text-muted-foreground"
          >
            <ImageOff className="h-4 w-4" aria-hidden />
            <span className="text-[11px] leading-tight">{photoLabel}</span>
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-4 py-3.5">
        <p
          className="truncate text-base font-semibold leading-snug tracking-tight text-foreground"
          data-testid="text-driver-invitation-car-title"
        >
          {title || t("booking.primary_driver.summary_car_missing")}
        </p>
        {dates ? (
          <p className="text-sm text-foreground/80" data-testid="text-driver-invitation-dates">
            {dates}
          </p>
        ) : null}
        {place ? (
          <p className="truncate text-sm text-muted-foreground" data-testid="text-driver-invitation-place">
            {place}
          </p>
        ) : null}
      </div>
    </section>
  );
}

export default DriverInvitationCarSummary;
